/* Bellabird infinite scroll for Symmetry 8.3.1
   Loads the next collection/search page as the shopper scrolls.
   Keeps Symmetry's existing filters/sorting/ajax pagination intact. */
(function () {
  'use strict';

  const SELECTORS = {
    section: '.shopify-section',
    filterContainer: 'filter-container',
    grid: '.collection-listing .product-grid',
    paginationRow: '.pagination-row',
    nextLink: '.pagination__next, .pagination a[rel="next"]'
  };

  const STATE = {
    loading: false,
    nextUrl: null,
    section: null,
    observer: null,
    sentinel: null,
    mutationObserver: null,
    initTimer: null
  };

  function isCollectionOrSearchPage() {
    return Boolean(document.querySelector(SELECTORS.filterContainer) && document.querySelector(SELECTORS.grid));
  }

  function getSection() {
    const filterContainer = document.querySelector(SELECTORS.filterContainer);
    return filterContainer ? filterContainer.closest(SELECTORS.section) : null;
  }

  function getGrid(section) {
    return section ? section.querySelector(SELECTORS.grid) : null;
  }

  function getPaginationRow(section) {
    if (!section) return null;
    const rows = Array.from(section.querySelectorAll(SELECTORS.paginationRow));
    return rows.length ? rows[rows.length - 1] : null;
  }

  function getNextUrl(section) {
    const paginationRow = getPaginationRow(section);
    const next = paginationRow ? paginationRow.querySelector(SELECTORS.nextLink) : null;
    return next ? next.href : null;
  }

  function ensureStyles() {
    if (document.getElementById('bellabird-infinite-scroll-styles')) return;
    const style = document.createElement('style');
    style.id = 'bellabird-infinite-scroll-styles';
    style.textContent = `
      .bellabird-infinite-scroll-sentinel {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 72px;
        width: 100%;
        clear: both;
      }
      .bellabird-infinite-scroll-loader {
        display: none;
        font-size: 0.9rem;
        letter-spacing: 0.04em;
        opacity: 0.75;
      }
      .bellabird-infinite-scroll-sentinel.is-loading .bellabird-infinite-scroll-loader {
        display: block;
      }
      .bellabird-infinite-scroll-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function buildSectionUrl(url, section) {
    const filterContainer = section ? section.querySelector(SELECTORS.filterContainer) : null;
    const sectionId = filterContainer ? filterContainer.dataset.filterSectionId : null;
    if (!sectionId) return url;

    const absoluteUrl = new URL(url, window.location.origin);
    absoluteUrl.searchParams.set('section_id', sectionId);
    return absoluteUrl.toString();
  }

  function stopObserver() {
    if (STATE.observer) {
      STATE.observer.disconnect();
      STATE.observer = null;
    }
  }

  function resetSentinel() {
    if (STATE.sentinel && STATE.sentinel.parentNode) {
      STATE.sentinel.parentNode.removeChild(STATE.sentinel);
    }
    STATE.sentinel = null;
  }

  function insertSentinel(section) {
    const paginationRow = getPaginationRow(section);
    if (!paginationRow) return null;

    resetSentinel();
    const sentinel = document.createElement('div');
    sentinel.className = 'bellabird-infinite-scroll-sentinel';
    sentinel.innerHTML = '<span class="bellabird-infinite-scroll-loader">Loading more products…</span>';
    paginationRow.insertAdjacentElement('beforebegin', sentinel);
    STATE.sentinel = sentinel;
    return sentinel;
  }

  function updatePaginationVisibility(section) {
    const paginationRow = getPaginationRow(section);
    if (!paginationRow) return;
    paginationRow.classList.toggle('bellabird-infinite-scroll-hidden', Boolean(STATE.nextUrl));
  }

  function copyNewProducts(currentGrid, newGrid) {
    const fragment = document.createDocumentFragment();
    Array.from(newGrid.children).forEach((productCard) => {
      fragment.appendChild(productCard);
    });
    currentGrid.appendChild(fragment);
  }

  function replacePagination(section, template) {
    const currentPagination = getPaginationRow(section);
    const newPaginationRows = Array.from(template.content.querySelectorAll(SELECTORS.paginationRow));
    const newPagination = newPaginationRows.length ? newPaginationRows[newPaginationRows.length - 1] : null;

    if (currentPagination && newPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    } else if (currentPagination) {
      currentPagination.innerHTML = '';
    }
  }

  function refreshThemeBits() {
    // Let other scripts/apps know new product cards were appended.
    document.dispatchEvent(new CustomEvent('bellabird:products-appended'));
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
  }

  async function loadNextPage() {
    if (STATE.loading || !STATE.nextUrl || !STATE.section) return;

    const section = STATE.section;
    const currentGrid = getGrid(section);
    if (!currentGrid) return;

    STATE.loading = true;
    if (STATE.sentinel) STATE.sentinel.classList.add('is-loading');

    try {
      const response = await fetch(buildSectionUrl(STATE.nextUrl, section), { method: 'get' });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const html = await response.text();
      const template = document.createElement('template');
      template.innerHTML = html;

      const newGrid = template.content.querySelector(SELECTORS.grid);
      if (newGrid) {
        copyNewProducts(currentGrid, newGrid);
      }

      replacePagination(section, template);
      STATE.nextUrl = getNextUrl(section);
      updatePaginationVisibility(section);
      refreshThemeBits();

      if (!STATE.nextUrl) {
        stopObserver();
        resetSentinel();
      }
    } catch (error) {
      console.warn('Bellabird infinite scroll error:', error);
      const paginationRow = getPaginationRow(section);
      if (paginationRow) paginationRow.classList.remove('bellabird-infinite-scroll-hidden');
      stopObserver();
    } finally {
      STATE.loading = false;
      if (STATE.sentinel) STATE.sentinel.classList.remove('is-loading');
    }
  }

  function setupObserver(section) {
    stopObserver();
    const sentinel = insertSentinel(section);
    if (!sentinel || !STATE.nextUrl) return;

    STATE.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) loadNextPage();
      });
    }, {
      root: null,
      rootMargin: '900px 0px',
      threshold: 0
    });

    STATE.observer.observe(sentinel);
  }

  function initInfiniteScroll() {
    clearTimeout(STATE.initTimer);
    STATE.initTimer = setTimeout(() => {
      if (!isCollectionOrSearchPage()) return;

      ensureStyles();
      STATE.section = getSection();
      STATE.nextUrl = getNextUrl(STATE.section);

      updatePaginationVisibility(STATE.section);
      if (STATE.nextUrl) {
        setupObserver(STATE.section);
      } else {
        stopObserver();
        resetSentinel();
      }
    }, 75);
  }

  function watchAjaxChanges() {
    const section = getSection();
    if (!section || STATE.mutationObserver) return;

    STATE.mutationObserver = new MutationObserver((mutations) => {
      const shouldRefresh = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          return node.nodeType === 1 && (
            node.matches?.(SELECTORS.grid) ||
            node.matches?.(SELECTORS.paginationRow) ||
            node.querySelector?.(SELECTORS.grid) ||
            node.querySelector?.(SELECTORS.paginationRow)
          );
        });
      });

      if (shouldRefresh) initInfiniteScroll();
    });

    STATE.mutationObserver.observe(section, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initInfiniteScroll();
    watchAjaxChanges();
  });

  document.addEventListener('shopify:section:load', () => {
    initInfiniteScroll();
    watchAjaxChanges();
  });
})();
